import { Controller } from '@hotwired/stimulus'
import { createErrorHintElement } from '../utils/dom'

export default class UploadFileController extends Controller {
  static targets = ['input', 'preview', 'errors', 'dropZone', 'error']
  static values = {
    maxSize: Number, // Максимальный размер файла в МБ
    maxItems: Number, // Максимальное количество файлов
    accept: String, // Допустимые типы файлов (например, 'jpg, png'),
    maxItemsText: String, // Текст для подсказки о максимальном количестве файлов
    maxSizeText: String, // Текст для подсказки о максимальном размере файлов
    acceptText: String, // Текст для подсказки о допустимых типах файлов
    removeBtnLabel: String // Текст для кнопки удаления файла
  }

  #errorHintElement = null

  connect() {
    this.files = []
    this.fileIds = new WeakMap()
    this.nextFileId = 0
    this.previewRenderId = 0
    this.previewUrls = new Set()
    this.setupAcceptAttribute()
  }

  disconnect() {
    this.revokePreviewUrls()
  }

  setupAcceptAttribute() {
    if (this.hasAcceptValue && this.acceptValue) {
      const extensions = this.acceptValue
        .split(',')
        .map((ext) => `.${ext.trim()}`)
      this.inputTarget.setAttribute('accept', extensions.join(','))
    }
  }

  // Обработчик выбора файла через input
  selectFile(event) {
    const files = Array.from(event.target.files)
    if (!files.length) return

    this.addFiles(files)
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add('is-dragging')
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('is-dragging')
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('is-dragging')

    const files = Array.from(event.dataTransfer.files)
    if (!files.length) return

    this.addFiles(files)
  }

  addFiles(newFiles) {
    const nextFiles = [...this.files, ...newFiles]
    if (!this.validate(nextFiles)) {
      this.updateInputFiles()
      return
    }

    newFiles.forEach((file) => {
      if (!this.fileIds.has(file)) {
        this.fileIds.set(file, String(this.nextFileId++))
      }
    })
    this.files = nextFiles
    this.renderPreview()

    // Отправляем событие об изменении файлов
    this.dispatch('changed', { detail: { files: this.files } })
  }

  removeFile(event) {
    const fileItem = event.target.closest('[data-file-id]')
    if (!fileItem) return

    const fileId = fileItem.dataset.fileId
    this.files = this.files.filter((file) => this.fileIds.get(file) !== fileId)

    this.validate()
    this.renderPreview()

    // Отправляем событие об изменении файлов
    this.dispatch('changed', { detail: { files: this.files } })
  }

  validate(files = this.files) {
    const errors = []

    const filesCount = files.length

    // Проверка количества файлов
    if (this.hasMaxItemsValue && filesCount > this.maxItemsValue) {
      errors.push(
        `${this.hasMaxItemsTextValue ? this.maxItemsTextValue : 'Max items'}: ${this.maxItemsValue}`
      )
    }

    // Проверка размера каждого файла
    if (this.hasMaxSizeValue) {
      const maxSizeBytes = this.maxSizeValue * 1024 * 1024
      const hasFileExceedingMaxSize = files.some(
        (file) => file.size > maxSizeBytes
      )
      if (hasFileExceedingMaxSize) {
        errors.push(
          `${this.hasMaxSizeTextValue ? this.maxSizeTextValue : 'Max size'}: ${this.maxSizeValue} MB`
        )
      }
    }

    // Проверка типов файлов
    if (this.hasAcceptValue && this.acceptValue) {
      const allowedExtensions = this.acceptValue
        .split(',')
        .map((ext) => ext.trim().toLowerCase())

      files.forEach((file) => {
        const lastDotIndex = file.name.lastIndexOf('.')
        const fileExtension =
          lastDotIndex >= 0 && lastDotIndex < file.name.length - 1
            ? file.name.slice(lastDotIndex + 1).toLowerCase()
            : ''
        if (!allowedExtensions.includes(fileExtension)) {
          errors.push(
            `${this.hasAcceptTextValue ? this.acceptTextValue : 'Invalid file type'}: ${file.name}`
          )
        }
      })
    }

    this.showErrors(errors)
    return errors.length === 0
  }

  showErrors(errors) {
    const hasErrors = errors.length > 0

    this.element.classList.toggle('is-invalid', hasErrors)

    if (this.#errorHintElement) {
      this.#errorHintElement.remove()
      this.#errorHintElement = null
    }

    if (this.hasErrorTarget) {
      this.errorTarget.remove()
    }

    if (this.hasErrorsTarget && hasErrors) {
      this.#errorHintElement = createErrorHintElement(
        errors[0],
        this.element.classList.contains('input--no-inverse')
      )
      this.errorsTarget.appendChild(this.#errorHintElement)
    }
  }

  // Отображение preview
  renderPreview() {
    if (!this.hasPreviewTarget) return

    // Очищаем preview
    this.revokePreviewUrls()
    this.previewTarget.innerHTML = ''
    const previewRenderId = ++this.previewRenderId

    this.files.forEach((file) => {
      this.renderFilePreview(file, previewRenderId)
    })

    this.updateInputFiles()
  }

  // Рендер preview одного файла
  renderFilePreview(file, previewRenderId) {
    if (previewRenderId !== this.previewRenderId) return

    const fileType = file.type.split('/')[0]
    const previewUrl = fileType === 'image' ? this.createPreviewUrl(file) : null
    const previewItem = this.createPreviewItem(file, previewUrl)
    this.previewTarget.appendChild(previewItem)

    if (fileType === 'image') {
      const imageElement = previewItem.querySelector('img')
      imageElement.addEventListener('load', () => {
        this.finishPreviewLoading(previewItem)
        this.revokePreviewUrl(previewUrl)
      })
      imageElement.addEventListener('error', () => {
        this.finishPreviewLoading(previewItem)
        this.revokePreviewUrl(previewUrl)
      })
      if (imageElement.complete) {
        this.finishPreviewLoading(previewItem)
        this.revokePreviewUrl(previewUrl)
      }
    } else {
      this.finishPreviewLoading(previewItem)
    }
  }

  createPreviewUrl(file) {
    const previewUrl = URL.createObjectURL(file)
    this.previewUrls.add(previewUrl)
    return previewUrl
  }

  revokePreviewUrl(previewUrl) {
    if (!previewUrl || !this.previewUrls.has(previewUrl)) return

    URL.revokeObjectURL(previewUrl)
    this.previewUrls.delete(previewUrl)
  }

  revokePreviewUrls() {
    this.previewUrls?.forEach((previewUrl) => URL.revokeObjectURL(previewUrl))
    this.previewUrls?.clear()
  }

  finishPreviewLoading(previewItem) {
    previewItem.querySelector('.upload-file__spinner')?.remove()
    previewItem.classList.remove('is-loading')
  }

  // Создание HTML для preview
  createPreviewItem(file, previewUrl) {
    const item = document.createElement('div')
    item.classList.add('upload-file__preview-item', 'is-loading')
    item.dataset.fileName = file.name
    item.dataset.fileId = this.fileIds.get(file)

    const previewElement = previewUrl
      ? this.createImagePreview(file, previewUrl)
      : this.createFilePreview(file)

    const spinner = document.createElement('div')
    spinner.className = 'upload-file__spinner'
    spinner.appendChild(this.createIcon('loader'))

    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'upload-file__remove-file'
    removeButton.setAttribute('data-action', 'click->upload-file#removeFile')
    removeButton.setAttribute(
      'aria-label',
      this.hasRemoveBtnLabelValue ? this.removeBtnLabelValue : 'Remove file'
    )
    removeButton.appendChild(this.createIcon('cross'))

    item.append(previewElement, spinner, removeButton)
    return item
  }

  createImagePreview(file, previewUrl) {
    const image = document.createElement('img')
    image.src = previewUrl
    image.setAttribute('alt', file.name)

    return image
  }

  createFilePreview(file) {
    const preview = document.createElement('span')
    const lastDotIndex = file.name.lastIndexOf('.')
    const fileExtension =
      lastDotIndex >= 0 && lastDotIndex < file.name.length - 1
        ? file.name.slice(lastDotIndex + 1).toUpperCase()
        : 'FILE'

    preview.className = 'upload-file__file-preview'
    preview.textContent = fileExtension

    return preview
  }

  createIcon(name) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', '1em')
    svg.setAttribute('height', '1em')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('data-icon', name)

    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use')
    use.setAttribute('href', `#icon-${name}`)
    svg.appendChild(use)

    return svg
  }

  // Обновление input.files
  updateInputFiles() {
    const dataTransfer = new DataTransfer()
    this.files.forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
  }

  reset() {
    this.files = []
    this.fileIds = new WeakMap()
    this.nextFileId = 0
    this.previewRenderId++
    this.revokePreviewUrls()
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
    }
    this.inputTarget.value = ''
    this.element.classList.remove('is-invalid')

    if (this.#errorHintElement) {
      this.#errorHintElement.remove()
      this.#errorHintElement = null
    }

    if (this.hasErrorTarget) {
      this.errorTarget.remove()
    }
  }
}
